import { useEffect, useState, useCallback } from "react";
import { Gift, Coins, Star, Award, Trophy, Crown, Loader2, History, Medal, Send, Clock, HandCoins } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { OfferCard } from "./PartnerOffersPanel";
import { useLang } from "../i18n/LanguageContext";

const pts = (n) => Number(n || 0).toLocaleString("en-US");

export const PortalLoyalty = () => {
  const { t, dir } = useLang();
  const cur = t("portal.currency");
  const money = (n) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} ${cur}`;
  const TIERS = {
    bronze: { name: t("portal.loyalty.bronze"), from: "from-amber-500", to: "to-amber-700", Icon: Star, next: 500 },
    silver: { name: t("portal.loyalty.silver"), from: "from-slate-400", to: "to-slate-600", Icon: Award, next: 1500 },
    gold: { name: t("portal.loyalty.gold"), from: "from-yellow-400", to: "to-amber-600", Icon: Trophy, next: 3000 },
    platinum: { name: t("portal.loyalty.platinum"), from: "from-indigo-400", to: "to-indigo-700", Icon: Crown, next: null },
  };
  const [data, setData] = useState(null);
  const [offers, setOffers] = useState([]);
  const [requesting, setRequesting] = useState(null);
  const reload = useCallback(() => { api.portalLoyalty().then(setData).catch(() => setData({ balance: 0, lifetime: 0, ledger: [], rewards: [], my_requests: [] })); }, []);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { api.portalOffers().then((d) => setOffers(d.offers || [])).catch(() => setOffers([])); }, []);

  const requestReward = async (r) => {
    setRequesting(r.id);
    try { await api.portalRedeemRequest(r.id); toast.success(t("portal.loyalty.sentRedeem")); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("portal.loyalty.sendFail")); }
    finally { setRequesting(null); }
  };

  if (!data) return <div className="py-16 text-center text-slate-300" data-testid="portal-loyalty-loading"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

  const pendingByReward = {};
  (data.my_requests || []).forEach((q) => { if (q.status === "pending") pendingByReward[q.reward_id] = q; });

  const T = TIERS[data.tier?.key] || TIERS.bronze;
  const progress = T.next ? Math.min(100, Math.round((data.lifetime / T.next) * 100)) : 100;
  const valueSar = (data.point_value_sar || 0) * data.balance;
  const tips = [t("portal.loyalty.tip1"), t("portal.loyalty.tip2"), t("portal.loyalty.tip3"), t("portal.loyalty.tip4")];

  return (
    <div className="space-y-5" data-testid="portal-loyalty" dir={dir}>
      <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-br ${T.from} ${T.to} p-6 text-white`}>
        <T.Icon className="absolute -left-4 -top-4 h-32 w-32 text-white/10" />
        <p className="text-sm text-white/80 flex items-center gap-1.5"><Gift className="h-4 w-4" /> {t("portal.loyalty.balanceTitle")}</p>
        <div className="mt-2 flex items-end gap-2">
          <span className="font-heading text-5xl font-bold font-mono-nums" data-testid="portal-loyalty-balance">{pts(data.balance)}</span>
          <span className="mb-2 text-white/80">{t("portal.loyalty.point")}</span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold"><T.Icon className="h-3 w-3" /> {t("portal.loyalty.tierPrefix")} {T.name}</span>
          {valueSar > 0 && <span className="text-xs text-white/80">≈ {money(valueSar)}</span>}
        </div>
        {T.next && (
          <div className="mt-4">
            <div className="flex justify-between text-[11px] text-white/80 mb-1"><span>{t("portal.loyalty.nextProgress")}</span><span className="font-mono-nums">{pts(data.lifetime)} / {pts(T.next)}</span></div>
            <div className="h-2 w-full rounded-full bg-white/20"><div className="h-full rounded-full bg-white transition-all" style={{ width: `${progress}%` }} /></div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
          <p className="text-xs text-emerald-700">{t("portal.loyalty.lifetime")}</p>
          <p className="font-heading text-2xl font-bold text-emerald-700 font-mono-nums mt-1">{pts(data.lifetime)}</p>
        </div>
        <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
          <p className="text-xs text-amber-700">{t("portal.loyalty.redeemed")}</p>
          <p className="font-heading text-2xl font-bold text-amber-700 font-mono-nums mt-1">{pts(data.redeemed)}</p>
        </div>
      </div>

      <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-white to-emerald-50/60 p-4" data-testid="portal-earn-behaviors">
        <p className="font-heading font-bold text-slate-800 mb-1 flex items-center gap-1.5"><HandCoins className="h-5 w-5 text-emerald-600" /> {t("portal.loyalty.howTitle")}</p>
        <p className="text-[11px] text-slate-400 mb-3">{t("portal.loyalty.howSub")}</p>
        <div className="space-y-2">
          {(data.earn_behaviors || []).map((b) => (
            <div key={b.id} data-testid={`portal-earn-${b.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-emerald-100 bg-white px-3 py-2">
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 text-sm truncate">{b.title}</p>
                {b.description && <p className="text-[11px] text-slate-400 truncate">{b.description}</p>}
              </div>
              <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-sm font-bold px-2.5 py-0.5 font-mono-nums">+{pts(b.points)}</span>
            </div>
          ))}
          {tips.map((tip, i) => (
            <div key={i} className="flex items-center gap-2 rounded-xl bg-white/70 px-3 py-2 text-sm text-slate-600">
              <Star className="h-4 w-4 text-amber-400 shrink-0" /> {tip}
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-3">{t("portal.loyalty.howNote")}</p>
      </div>

      {offers.length > 0 && (
        <div data-testid="portal-offers">
          <p className="font-heading font-bold text-slate-800 mb-2 flex items-center gap-1.5"><Gift className="h-4 w-4 text-indigo-600" /> {t("portal.loyalty.offers")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {offers.map((o) => <OfferCard key={o.id} offer={o} />)}
          </div>
        </div>
      )}

      {data.leaderboard?.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-white to-amber-50/50 p-4" data-testid="portal-leaderboard">
          <div className="flex items-center justify-between mb-3">
            <p className="font-heading font-bold text-slate-800 flex items-center gap-1.5"><Trophy className="h-5 w-5 text-amber-500" /> {t("portal.loyalty.leaderboard")}</p>
            {data.my_rank && <span className="rounded-full bg-amber-100 text-amber-800 text-xs font-bold px-2.5 py-1" data-testid="portal-my-rank">{t("portal.loyalty.myRank", { n: data.my_rank })}</span>}
          </div>
          <div className="space-y-2">
            {data.leaderboard.map((b) => (
              <div key={b.employee_id} className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${b.rank <= 3 ? "border-amber-200 bg-white shadow-sm" : "border-slate-100 bg-white"}`}>
                <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-full font-bold font-mono-nums text-sm ${b.rank <= 3 ? (b.rank === 1 ? "bg-gradient-to-br from-yellow-400 to-amber-500 text-white" : b.rank === 2 ? "bg-gradient-to-br from-slate-300 to-slate-400 text-white" : "bg-gradient-to-br from-amber-600 to-orange-700 text-white") : "bg-slate-100 text-slate-500"}`}>
                  {b.rank <= 3 ? <Medal className="h-4 w-4" /> : b.rank}
                </div>
                <p className="min-w-0 flex-1 font-semibold text-slate-800 truncate">{b.name}</p>
                <span className="font-mono-nums font-bold text-amber-700 shrink-0">{pts(b.points)} {t("portal.loyalty.point")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.rewards?.length > 0 && (
        <div>
          <p className="font-heading font-bold text-slate-800 mb-2 flex items-center gap-1.5"><Coins className="h-4 w-4 text-amber-600" /> {t("portal.loyalty.rewards")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.rewards.map((r) => {
              const afford = data.balance >= r.cost;
              const pending = pendingByReward[r.id];
              return (
                <div key={r.id} data-testid={`portal-reward-${r.id}`} className={`rounded-2xl border p-4 ${afford ? "border-emerald-200 bg-white" : "border-slate-200 bg-slate-50"}`}>
                  <div className="flex items-start justify-between">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-amber-400 to-rose-500 text-white"><Gift className="h-5 w-5" /></div>
                    <span className="font-mono-nums text-sm font-bold text-amber-700">{pts(r.cost)} {t("portal.loyalty.point")}</span>
                  </div>
                  <p className="mt-2 font-semibold text-slate-800">{r.title}</p>
                  {r.description && <p className="text-xs text-slate-400">{r.description}</p>}
                  {pending ? (
                    <div className="mt-3 flex items-center justify-center gap-1.5 rounded-lg bg-amber-50 text-amber-700 text-xs font-semibold py-2" data-testid={`portal-reward-pending-${r.id}`}>
                      <Clock className="h-3.5 w-3.5" /> {t("portal.loyalty.pendingReview")}
                    </div>
                  ) : afford ? (
                    <button onClick={() => requestReward(r)} disabled={requesting === r.id} data-testid={`portal-reward-request-${r.id}`}
                      className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-2 transition disabled:opacity-60">
                      {requesting === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("portal.loyalty.redeemBtn")}
                    </button>
                  ) : (
                    <p className="mt-3 text-[11px] font-semibold text-slate-400 text-center">{t("portal.loyalty.needMore", { n: pts(r.cost - data.balance) })}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <p className="font-heading font-bold text-slate-800 mb-2 flex items-center gap-1.5"><History className="h-4 w-4 text-blue-600" /> {t("portal.loyalty.ledgerTitle")}</p>
        {data.ledger?.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white py-10 text-center text-slate-400 text-sm" data-testid="portal-loyalty-empty">{t("portal.loyalty.ledgerEmpty")}</div>
        ) : (
          <div className="space-y-2">
            {data.ledger.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-white px-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm text-slate-700 truncate">{r.reason || (r.points >= 0 ? t("portal.loyalty.perfPoints") : t("portal.loyalty.spendReward"))}{r.reward_title ? ` · ${r.reward_title}` : ""}</p>
                  <p className="text-[10px] text-slate-400">{(r.created_at || "").slice(0, 10)}</p>
                </div>
                <span className={`font-mono-nums font-bold ${r.points >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{r.points >= 0 ? "+" : ""}{pts(r.points)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
